/* eslint-disable formatjs/no-literal-string-in-jsx --
 * Legal docs untranslated */
import type { ReactElement } from 'react';
import { ODSContainer } from 'bc-ods';

function Terms(): ReactElement {
  return (
    <div className="h-100 w-100 overflow-y-auto">
      <ODSContainer
        data-testid="content-terms-and-conditions"
        className="mt-5"
        style={{ maxWidth: '100ch' }}
      >
        <h1>Terms of Service</h1>

        <p className="small">Last updated September 25, 2023</p>

        <p>
          Thanks for using Onebrief! These terms of service (&quot;Terms&quot;) cover your use
          access to our services and websites (&quot;Services&quot;). We also have a{' '}
          <a href="./privacy">Privacy Policy</a>, which explains how we collect and use your
          information. By using our Services, you&apos;re agreeing to be bound by both these Terms
          and our <a href="./privacy">Privacy Policy</a>. If you&apos;re using our Services for an
          organization, you&apos;re agreeing to these Terms on behalf of that organization.
        </p>

        <h2>Your Content</h2>

        <p>
          When you use our Services, you provide us with data like your text, messages, images, and
          so on (&quot;Your Content”). Your Content is yours. These Terms don&apos;t give us any
          rights to Your Content except for the limited rights that enable us to offer the Services.
        </p>

        <p>
          We need your permission to host Your Content, back it up, and share it when you ask us to.
          Our Services also provide you with features that require our systems to access or store
          Your Content. You give us permission to do these things, and this permission extends to
          trusted third parties we work with.
        </p>

        <p>
          You&apos;re responsible for your conduct while using our Services. Others may have
          intellectual property rights to Content in our Services. Please copy or upload content
          unless you have the right to do so. We aren&apos;t responsible for the content people
          create and share via the Services.
        </p>

        <p>
          Help us safeguard Your Content. Safeguard your password to the Services, and keep your
          account information current. Don&apos;t share your credentials or give others access to
          your account.
        </p>

        <p>
          You may use our Services only as permitted by applicable law. Finally, to use our
          Services, you must be at least 13, or in some cases, even older. Please check your local
          law for the age of digital consent. If you don&apos;t meet these age requirements, you may
          not use the Services.
        </p>

        <h2>User Responsibility</h2>
        <p>
          Users who add other users to a plan or planning team acknowledge and agree that they are
          solely responsible for ensuring team members have appropriate clearance access and need to
          know in accordance with 50 U.S. Code § 3341, DoD Instruction 5200.01, DoD Manual 5200.01,
          Volume 1, Volume 2, Volume 3, DoD Instruction 5230.24, DoD Instruction 5200.48, 32 Code of
          Federal Regulations, Part 2002, Executive Order 13556, as well as any unit-specific
          policies and regulations applicable to your organization or directorate.
        </p>

        <h2>Compliance</h2>
        <p>
          You agree to comply with all applicable laws, regulations, and policies related to the use
          of the Application, including those mentioned in section 1.1. You understand and
          acknowledge that any violation of these laws, regulations, or policies may result in legal
          consequences and termination of your access to Onebrief.
        </p>

        <h2>Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless Onebrief, its officers, directors, employees, and
          affiliates from any claims, damages, liabilities, losses, or expenses arising out of or in
          connection with your failure to ensure appropriate clearance access and compliance with
          the aforementioned laws, regulations, and policies.
        </p>

        <h2>Beta Services</h2>

        <p>
          We are currently offering products and features that we are still testing and evaluating.
          Those Services have been marked beta or early access (or with words or phrases with
          similar meanings). Please keep in mind that they may not be as reliable as software that
          has already completed the beta stage.
        </p>

        <h2>Your Feedback</h2>

        <p>
          You may provide feedback to Onebrief about your use and experience while using the
          Services. Unless Onebrief otherwise agrees in writing, you hereby agree that Onebrief
          shall own all feedback, comments, suggestions, recommendations, ideas, concepts and
          changes that you provide to Onebrief regarding your use and experience while using the
          Services and all associated intellectual property rights (collectively the
          &quot;Feedback&quot;) and you hereby assign to Onebrief all of your right, title and
          interest thereto.
        </p>

        <h2>Copyright</h2>

        <p>
          We respect the intellectual property of others and ask that you do too. We respond to
          notices of alleged copyright infringement if they comply with the law, and such notices
          should be reported using our Copyright Policy. We reserve the right to delete or disable
          content alleged to be infringing and terminate accounts of repeat infringers. Our
          designated agent for notice of alleged copyright infringement on the Services is:
        </p>

        <blockquote>
          Grant Demaree
          <br />
          Onebrief, Inc.
          <br />
          701 Brazos St
          <br />
          Austin, TX 78701
          <br />
          <a href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#68;&#77;&#67;&#65;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;">
            {/* eslint-disable-next-line max-len */}
            &#68;&#77;&#67;&#65;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;
          </a>
        </blockquote>

        <h2>Paid Accounts</h2>

        <p>
          All Onebrief services are provided as a free trial or as Paid Accounts exchange for
          payment.
        </p>

        <p>
          <strong>Billing.</strong> We&apos;ll automatically bill you on a periodic basis from the
          date you purchase a Paid Account and on each periodic renewal until cancellation. If
          you&apos;re on an annual plan, we&apos;ll send you a notice email reminding you that your
          plan is about to renew within a reasonable period of time prior to the renewal date.
          You&apos;re responsible for all applicable taxes, and we&apos;ll charge tax when required
          to do so. Some countries have mandatory local laws regarding your cancellation rights, and
          this paragraph doesn&apos;t override these laws.
        </p>

        <p>
          <strong>No Refunds.</strong> You may cancel your Onebrief account at any time. Refunds are
          only issued if required by law. For example, users living in the European Union have the
          right to cancel their Paid Account subscriptions within 14 days of signing up for,
          upgrading to, or renewing a Paid Account.
        </p>

        <p>
          <strong>Cancellation.</strong> Your Paid Account will remain in effect until it&apos;s
          cancelled or terminated under these Terms. If you don&apos;t pay for your Paid Account on
          time, we reserve the right to suspend it.
        </p>

        <p>
          <strong>Changes.</strong> We may change the fees in effect but will give you advance
          notice of these changes via a message to the email address associated with your account.
        </p>

        <h2>Termination</h2>

        <p>
          You&apos;re free to stop using our Services at any time. We reserve the right to suspend
          or terminate your access to the Services with notice to you if:
        </p>

        <ul className="list-unstyled">
          <li>(a) you&apos;re in breach of these Terms, or</li>

          <li>
            (b) you&apos;re using the Services in a manner that would cause a real risk of harm or
            loss to us or other users.
          </li>
        </ul>

        <p>
          We&apos;ll provide you with reasonable advance notice via the email address associated
          with your account to remedy the activity that prompted us to contact you and give you the
          opportunity to export Your Content from our Services. If after such notice you fail to
          take the steps we ask of you, we&apos;ll terminate or suspend your access to the Services.
        </p>

        <p>We won&apos;t provide notice before termination where:</p>

        <ul className="list-unstyled">
          <li>(a) you&apos;re in material breach of these Terms,</li>

          <li>
            (b) doing so would cause us legal liability or compromise our ability to provide the
            Services to our other users, or
          </li>

          <li>(c) we&apos;re prohibited from doing so by law.</li>
        </ul>

        <h2>Discontinuation of Services</h2>

        <p>
          We may decide to discontinue the Services, and these Terms create no obligation for us to
          continue developing or providing the Services. If we do so, we&apos;ll give you reasonable
          prior notice so that you can export Your Content from our systems. If we discontinue
          Services in this way before the end of any fixed or minimum term you have paid us for,
          we&apos;ll refund the portion of the fees you have pre-paid but haven&apos;t received
          Services for.
        </p>

        <h2>Services “AS IS”</h2>

        <p>
          We strive to provide great Services, but there are certain things that we can&apos;t
          guarantee. TO THE FULLEST EXTENT PERMITTED BY LAW, ONEBRIEF AND ITS AFFILIATES, SUPPLIERS
          AND DISTRIBUTORS MAKE NO WARRANTIES, EITHER EXPRESS OR IMPLIED, ABOUT THE SERVICES. THE
          SERVICES ARE PROVIDED &quot;AS IS.&quot; WE ALSO DISCLAIM ANY WARRANTIES OF
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
        </p>

        <h2>Limitation of Liability</h2>

        <p>
          WE DON&apos;T EXCLUDE OR LIMIT OUR LIABILITY TO YOU WHERE IT WOULD BE ILLEGAL TO DO
          SO—THIS INCLUDES ANY LIABILITY FOR ONEBRIEF&apos;S OR ITS AFFILIATES&apos; FRAUD OR
          FRAUDULENT MISREPRESENTATION IN PROVIDING THE SERVICES. IN COUNTRIES WHERE THE FOLLOWING
          TYPES OF EXCLUSIONS AREN&apos;T ALLOWED, WE&apos;RE RESPONSIBLE TO YOU ONLY FOR LOSSES AND
          DAMAGES THAT ARE A REASONABLY FORESEEABLE RESULT OF OUR FAILURE TO USE REASONABLE CARE AND
          SKILL OR OUR BREACH OF OUR CONTRACT WITH YOU. THIS PARAGRAPH DOESN&apos;T AFFECT CONSUMER
          RIGHTS THAT CAN&apos;T BE WAIVED OR LIMITED BY ANY CONTRACT OR AGREEMENT.
        </p>

        <p>
          IN COUNTRIES WHERE EXCLUSIONS OR LIMITATIONS OF LIABILITY ARE ALLOWED, ONEBRIEF, ITS
          AFFILIATES, SUPPLIERS OR DISTRIBUTORS WON&apos;T BE LIABLE FOR:
        </p>

        <ul className="list-unstyled">
          <li>
            i. ANY INDIRECT, SPECIAL, INCIDENTAL, PUNITIVE, EXEMPLARY, OR CONSEQUENTIAL DAMAGES, OR
          </li>

          <li>ii. ANY LOSS OF USE, DATA, BUSINESS, OR PROFITS, REGARDLESS OF LEGAL THEORY.</li>
        </ul>

        <p>
          THESE EXCLUSIONS OR LIMITATIONS WILL APPLY REGARDLESS OF WHETHER OR NOT ONEBRIEF OR ANY OF
          ITS AFFILIATES HAS BEEN WARNED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>

        <p>
          IF YOU USE THE SERVICES FOR ANY COMMERCIAL, BUSINESS, OR RE-SALE PURPOSE, ONEBRIEF, ITS
          AFFILIATES, SUPPLIERS OR DISTRIBUTORS WILL HAVE NO LIABILITY TO YOU FOR ANY LOSS OF
          PROFIT, LOSS OF BUSINESS, BUSINESS INTERRUPTION, OR LOSS OF BUSINESS OPPORTUNITY. ONEBRIEF
          AND ITS AFFILIATES AREN&apos;T RESPONSIBLE FOR THE CONDUCT, WHETHER ONLINE OR OFFLINE, OF
          ANY USER OF THE SERVICES.
        </p>

        <p>
          OTHER THAN FOR THE TYPES OF LIABILITY WE CANNOT LIMIT BY LAW (AS DESCRIBED IN THIS
          SECTION), WE LIMIT OUR LIABILITY TO YOU TO THE GREATER OF $20 USD OR 100% OF ANY AMOUNT
          YOU&apos;VE PAID UNDER YOUR CURRENT SERVICE PLAN WITH ONEBRIEF.
        </p>

        <h2>Resolving Disputes</h2>

        <p>
          <strong>Let&apos;s Try To Sort Things Out First.</strong> We want to address your concerns
          without needing a formal legal case. Before filing a claim against Onebrief, you agree to
          try to resolve the dispute informally by contacting{' '}
          <a href="&#109;&#97;&#105;&#108;&#116;&#111;&#58;&#108;&#101;&#103;&#97;&#108;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;">
            {/* eslint-disable-next-line max-len */}
            &#108;&#101;&#103;&#97;&#108;&#64;&#111;&#110;&#101;&#98;&#114;&#105;&#101;&#102;&#46;&#99;&#111;&#109;
          </a>
          . We&apos;ll try to resolve the dispute informally by contacting you via email. If a
          dispute is not resolved within 15 days of submission, you or Onebrief may bring a formal
          proceeding.
        </p>

        <p>
          <strong>Judicial forum for disputes.</strong> You and Onebrief agree that any judicial
          proceeding to resolve claims relating to these Terms or the Services will be brought in
          the federal or state courts of Travis County, Texas. Both you and Onebrief consent to
          venue and personal jurisdiction in such courts. If you reside in a country (for example,
          European Union member states) with laws that give consumers the right to bring disputes in
          their local courts, this paragraph doesn&apos;t affect those requirements.
        </p>

        <h2>Controlling Law</h2>

        <p>
          These Terms will be governed by the laws of the state of Texas and applicable federal
          laws. However, some countries (including those in the European Union) have laws that
          require agreements to be governed by the local laws of the consumer&apos;s country. This
          paragraph doesn&apos;t override those laws.
        </p>

        <h2>Entire Agreement</h2>

        <p>
          These Terms constitute the entire agreement between you and Onebrief with respect to the
          subject matter of these Terms, and supersede and replace any other prior or
          contemporaneous agreements, or terms and conditions applicable to the subject matter of
          these Terms. These Terms create no third party beneficiary rights.
        </p>

        <h2>Waiver, Severability & Assignment</h2>

        <p>
          Onebrief&apos;s failure to enforce a provision is not a waiver of its right to do so
          later. If a provision is found unenforceable, the remaining provisions of the Terms will
          remain in full effect and an enforceable term will be substituted reflecting our intent as
          closely as possible. You may not assign any of your rights under these Terms, and any such
          attempt will be void. Onebrief may assign its rights to any of its affiliates or
          subsidiaries, or to any successor in interest of any business associated with the
          Services.
        </p>

        <h2>Modifications</h2>

        <p>We may revise these Terms from time to time to better reflect:</p>

        <ul className="list-unstyled">
          <li>(a) changes to the law,</li>

          <li>(b) new regulatory requirements, or</li>

          <li>(c) improvements or enhancements made to our Services.</li>
        </ul>

        <p>
          If an update affects your use of the Services or your legal rights as a user of our
          Services, we&apos;ll notify you prior to the update&apos;s effective date by sending an
          email to the email address associated with your account or via an in-product notification.
          These updated terms will be effective no less than 30 days from when we notify you.
        </p>

        <p>
          If you don&apos;t agree to the updates we make, please cancel your account before they
          become effective. Where applicable, we&apos;ll offer you a prorated refund based on the
          amounts you have prepaid for Services and your account cancellation date. By continuing to
          use or access the Services after the updates come into effect, you agree to be bound by
          the revised Terms.
        </p>
      </ODSContainer>
    </div>
  );
}

export default Terms;
